// Comprehensive Timezone List for Mission Control Health Coaching Platform
// Based on Infrastructure Enhancement documentation from reference codebase

export interface TimezoneOption {
  value: string;        // IANA timezone identifier
  label: string;        // Display name
  region: string;       // Geographic region
  keywords: string[];   // Search keywords
  utcOffset: string;    // UTC offset display
}

// 40+ Comprehensive Timezone List with search keywords
export const COMPREHENSIVE_TIMEZONES: TimezoneOption[] = [
  // North America - Eastern
  {
    value: 'America/New_York',
    label: 'Eastern Time (New York)',
    region: 'North America',
    keywords: ['eastern', 'est', 'edt', 'new york', 'nyc', 'toronto'],
    utcOffset: 'UTC-5/-4'
  },
  
  // North America - Central
  {
    value: 'America/Chicago',
    label: 'Central Time (Chicago)',
    region: 'North America',
    keywords: ['central', 'cst', 'cdt', 'chicago', 'dallas', 'houston'],
    utcOffset: 'UTC-6/-5'
  },
  
  // North America - Mountain
  {
    value: 'America/Denver',
    label: 'Mountain Time (Denver)',
    region: 'North America',
    keywords: ['mountain', 'mst', 'mdt', 'denver', 'calgary', 'phoenix'],
    utcOffset: 'UTC-7/-6'
  },
  
  // North America - Pacific
  {
    value: 'America/Los_Angeles',
    label: 'Pacific Time (Los Angeles)',
    region: 'North America',
    keywords: ['pacific', 'pst', 'pdt', 'los angeles', 'san francisco', 'seattle', 'vancouver'],
    utcOffset: 'UTC-8/-7'
  },
  
  // North America - Alaska
  {
    value: 'America/Anchorage',
    label: 'Alaska Time (Anchorage)',
    region: 'North America',
    keywords: ['alaska', 'akst', 'akdt', 'anchorage'],
    utcOffset: 'UTC-9/-8'
  },
  
  // North America - Hawaii
  {
    value: 'Pacific/Honolulu',
    label: 'Hawaii Time (Honolulu)',
    region: 'North America',
    keywords: ['hawaii', 'hst', 'honolulu'],
    utcOffset: 'UTC-10'
  },
  
  // Europe - Western
  {
    value: 'Europe/London',
    label: 'Greenwich Mean Time (London)',
    region: 'Europe',
    keywords: ['london', 'uk', 'britain', 'gmt', 'bst', 'greenwich'],
    utcOffset: 'UTC+0/+1'
  },
  
  {
    value: 'Europe/Dublin',
    label: 'Irish Standard Time (Dublin)',
    region: 'Europe',
    keywords: ['dublin', 'ireland', 'irish', 'ist'],
    utcOffset: 'UTC+0/+1'
  },
  
  // Europe - Central
  {
    value: 'Europe/Paris',
    label: 'Central European Time (Paris)',
    region: 'Europe',
    keywords: ['paris', 'france', 'cet', 'cest', 'central european'],
    utcOffset: 'UTC+1/+2'
  },
  
  {
    value: 'Europe/Berlin',
    label: 'Central European Time (Berlin)',
    region: 'Europe',
    keywords: ['berlin', 'germany', 'cet', 'cest', 'german'],
    utcOffset: 'UTC+1/+2'
  },
  
  {
    value: 'Europe/Rome',
    label: 'Central European Time (Rome)',
    region: 'Europe',
    keywords: ['rome', 'italy', 'italian', 'cet', 'cest'],
    utcOffset: 'UTC+1/+2'
  },
  
  {
    value: 'Europe/Madrid',
    label: 'Central European Time (Madrid)',
    region: 'Europe',
    keywords: ['madrid', 'spain', 'spanish', 'cet', 'cest'],
    utcOffset: 'UTC+1/+2'
  },
  
  {
    value: 'Europe/Amsterdam',
    label: 'Central European Time (Amsterdam)',
    region: 'Europe',
    keywords: ['amsterdam', 'netherlands', 'dutch', 'cet', 'cest'],
    utcOffset: 'UTC+1/+2'
  },
  
  {
    value: 'Europe/Zurich',
    label: 'Central European Time (Zurich)',
    region: 'Europe',
    keywords: ['zurich', 'switzerland', 'swiss', 'cet', 'cest'],
    utcOffset: 'UTC+1/+2'
  },
  
  // Europe - Eastern
  {
    value: 'Europe/Kiev',
    label: 'Eastern European Time (Kiev)',
    region: 'Europe',
    keywords: ['kiev', 'ukraine', 'eet', 'eest', 'eastern european'],
    utcOffset: 'UTC+2/+3'
  },
  
  {
    value: 'Europe/Helsinki',
    label: 'Eastern European Time (Helsinki)',
    region: 'Europe',
    keywords: ['helsinki', 'finland', 'finnish', 'eet', 'eest'],
    utcOffset: 'UTC+2/+3'
  },
  
  {
    value: 'Europe/Athens',
    label: 'Eastern European Time (Athens)',
    region: 'Europe',
    keywords: ['athens', 'greece', 'greek', 'eet', 'eest'],
    utcOffset: 'UTC+2/+3'
  },
  
  // Asia - Middle East
  {
    value: 'Asia/Dubai',
    label: 'Gulf Standard Time (Dubai)',
    region: 'Asia',
    keywords: ['dubai', 'uae', 'gulf', 'gst'],
    utcOffset: 'UTC+4'
  },
  
  {
    value: 'Asia/Tehran',
    label: 'Iran Standard Time (Tehran)',
    region: 'Asia',
    keywords: ['tehran', 'iran', 'iranian', 'irst'],
    utcOffset: 'UTC+3:30/+4:30'
  },
  
  // Asia - India/Central
  {
    value: 'Asia/Kolkata',
    label: 'India Standard Time (Kolkata)',
    region: 'Asia',
    keywords: ['kolkata', 'india', 'indian', 'ist', 'mumbai', 'delhi'],
    utcOffset: 'UTC+5:30'
  },
  
  {
    value: 'Asia/Karachi',
    label: 'Pakistan Standard Time (Karachi)',
    region: 'Asia',
    keywords: ['karachi', 'pakistan', 'pakistani', 'pkt'],
    utcOffset: 'UTC+5'
  },
  
  // Asia - East Asia
  {
    value: 'Asia/Shanghai',
    label: 'China Standard Time (Shanghai)',
    region: 'Asia',
    keywords: ['shanghai', 'china', 'chinese', 'cst', 'beijing'],
    utcOffset: 'UTC+8'
  },
  
  {
    value: 'Asia/Tokyo',
    label: 'Japan Standard Time (Tokyo)',
    region: 'Asia',
    keywords: ['tokyo', 'japan', 'japanese', 'jst'],
    utcOffset: 'UTC+9'
  },
  
  {
    value: 'Asia/Seoul',
    label: 'Korea Standard Time (Seoul)',
    region: 'Asia',
    keywords: ['seoul', 'korea', 'korean', 'kst'],
    utcOffset: 'UTC+9'
  },
  
  {
    value: 'Asia/Singapore',
    label: 'Singapore Standard Time (Singapore)',
    region: 'Asia',
    keywords: ['singapore', 'sgt'],
    utcOffset: 'UTC+8'
  },
  
  {
    value: 'Asia/Hong_Kong',
    label: 'Hong Kong Time (Hong Kong)',
    region: 'Asia',
    keywords: ['hong kong', 'hk', 'hkt'],
    utcOffset: 'UTC+8'
  },
  
  {
    value: 'Asia/Bangkok',
    label: 'Indochina Time (Bangkok)',
    region: 'Asia',
    keywords: ['bangkok', 'thailand', 'thai', 'ict'],
    utcOffset: 'UTC+7'
  },
  
  // Australia/Oceania
  {
    value: 'Australia/Sydney',
    label: 'Australian Eastern Time (Sydney)',
    region: 'Australia/Oceania',
    keywords: ['sydney', 'australia', 'australian', 'aest', 'aedt', 'melbourne'],
    utcOffset: 'UTC+10/+11'
  },
  
  {
    value: 'Australia/Perth',
    label: 'Australian Western Time (Perth)',
    region: 'Australia/Oceania',
    keywords: ['perth', 'australia', 'australian', 'awst'],
    utcOffset: 'UTC+8'
  },
  
  {
    value: 'Australia/Adelaide',
    label: 'Australian Central Time (Adelaide)',
    region: 'Australia/Oceania',
    keywords: ['adelaide', 'australia', 'australian', 'acst', 'acdt'],
    utcOffset: 'UTC+9:30/+10:30'
  },
  
  {
    value: 'Pacific/Auckland',
    label: 'New Zealand Time (Auckland)',
    region: 'Australia/Oceania',
    keywords: ['auckland', 'new zealand', 'nzst', 'nzdt'],
    utcOffset: 'UTC+12/+13'
  },
  
  // South America
  {
    value: 'America/Sao_Paulo',
    label: 'Brasilia Time (São Paulo)',
    region: 'South America',
    keywords: ['sao paulo', 'brazil', 'brazilian', 'brt'],
    utcOffset: 'UTC-3/-2'
  },
  
  {
    value: 'America/Buenos_Aires',
    label: 'Argentina Time (Buenos Aires)',
    region: 'South America',
    keywords: ['buenos aires', 'argentina', 'argentinian', 'art'],
    utcOffset: 'UTC-3'
  },
  
  {
    value: 'America/Lima',
    label: 'Peru Time (Lima)',
    region: 'South America',
    keywords: ['lima', 'peru', 'peruvian', 'pet'],
    utcOffset: 'UTC-5'
  },
  
  // Africa
  {
    value: 'Africa/Cairo',
    label: 'Eastern European Time (Cairo)',
    region: 'Africa',
    keywords: ['cairo', 'egypt', 'egyptian', 'eet'],
    utcOffset: 'UTC+2'
  },
  
  {
    value: 'Africa/Lagos',
    label: 'West Africa Time (Lagos)',
    region: 'Africa',
    keywords: ['lagos', 'nigeria', 'nigerian', 'wat'],
    utcOffset: 'UTC+1'
  },
  
  {
    value: 'Africa/Johannesburg',
    label: 'South Africa Standard Time (Johannesburg)',
    region: 'Africa',
    keywords: ['johannesburg', 'south africa', 'sast'],
    utcOffset: 'UTC+2'
  },
  
  // Additional North America
  {
    value: 'America/Mexico_City',
    label: 'Central Standard Time (Mexico City)',
    region: 'North America',
    keywords: ['mexico city', 'mexico', 'mexican', 'cst', 'cdt'],
    utcOffset: 'UTC-6/-5'
  },
  
  {
    value: 'America/Toronto',
    label: 'Eastern Standard Time (Toronto)',
    region: 'North America',
    keywords: ['toronto', 'canada', 'canadian', 'est', 'edt'],
    utcOffset: 'UTC-5/-4'
  },
  
  {
    value: 'America/Vancouver',
    label: 'Pacific Standard Time (Vancouver)',
    region: 'North America',
    keywords: ['vancouver', 'canada', 'canadian', 'pst', 'pdt'],
    utcOffset: 'UTC-8/-7'
  },
  
  // UTC
  {
    value: 'UTC',
    label: 'Coordinated Universal Time (UTC)',
    region: 'Universal',
    keywords: ['utc', 'universal', 'coordinated', 'greenwich', 'gmt'],
    utcOffset: 'UTC+0'
  }
];

// Search function to filter timezones based on query
export function searchTimezones(query: string, timezones: TimezoneOption[] = COMPREHENSIVE_TIMEZONES): TimezoneOption[] {
  if (!query || query.trim().length === 0) {
    return timezones;
  }

  const searchTerm = query.toLowerCase().trim();
  
  return timezones.filter(timezone => {
    // Search in label
    if (timezone.label.toLowerCase().includes(searchTerm)) {
      return true;
    }
    
    // Search in region
    if (timezone.region.toLowerCase().includes(searchTerm)) {
      return true;
    }
    
    // Search in keywords
    if (timezone.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm))) {
      return true;
    }
    
    // Search in timezone value (IANA identifier)
    if (timezone.value.toLowerCase().includes(searchTerm)) {
      return true;
    }
    
    // Search in UTC offset
    if (timezone.utcOffset.toLowerCase().includes(searchTerm)) {
      return true;
    }
    
    return false;
  });
}

// Get timezone option by value
export function getTimezoneOption(value: string, timezones: TimezoneOption[] = COMPREHENSIVE_TIMEZONES): TimezoneOption | null {
  return timezones.find(timezone => timezone.value === value) || null;
}

// Get timezone abbreviation from IANA identifier
export function getTimezoneAbbreviation(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    
    const parts = formatter.formatToParts(now);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    
    return timeZonePart ? timeZonePart.value : timezone;
  } catch (error) {
    // Fallback for invalid timezone
    console.warn(`Invalid timezone: ${timezone}`);
    return timezone;
  }
}

// Export type for use in other components
export type { TimezoneOption };